import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { transformImage } from "@/lib/imageUrl";
import {
  groupPhotosByArea,
  type PhotoEntry,
  type PhotoGroup,
} from "@/lib/photoAreas";

interface PhotoTourModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  photos: PhotoEntry[];
  title: string;
  /** Initial flat-array index when opening in zoom mode. */
  initialIndex?: number;
  /** Default view mode when opened. Defaults to "tour". */
  initialMode?: "tour" | "zoom";
}

const PhotoTourModal = ({
  open,
  onOpenChange,
  photos,
  title,
  initialIndex = 0,
  initialMode = "tour",
}: PhotoTourModalProps) => {
  const [viewMode, setViewMode] = useState<"tour" | "zoom">(initialMode);
  const [zoomIndex, setZoomIndex] = useState<number>(initialIndex);

  const groups = useMemo(() => groupPhotosByArea(photos), [photos]);

  // Reset to the requested initial mode whenever the modal is opened.
  useEffect(() => {
    if (open) {
      setViewMode(initialMode);
      setZoomIndex(initialIndex);
    }
  }, [open, initialMode, initialIndex]);

  const openZoom = useCallback((photoIndex: number) => {
    setZoomIndex(photoIndex);
    setViewMode("zoom");
  }, []);

  const closeZoom = useCallback(() => setViewMode("tour"), []);

  const prevZoom = useCallback(() => {
    if (photos.length === 0) return;
    setZoomIndex((i) => (i === 0 ? photos.length - 1 : i - 1));
  }, [photos.length]);

  const nextZoom = useCallback(() => {
    if (photos.length === 0) return;
    setZoomIndex((i) => (i === photos.length - 1 ? 0 : i + 1));
  }, [photos.length]);

  // Keyboard nav for the zoom layer. ESC returns to tour (matches Airbnb).
  useEffect(() => {
    if (!open || viewMode !== "zoom") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevZoom();
      else if (e.key === "ArrowRight") nextZoom();
      else if (e.key === "Escape") {
        e.stopPropagation();
        closeZoom();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, viewMode, prevZoom, nextZoom, closeZoom]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* z-[130]: above the navbar (z-[60]), the mobile menu (z-[70]) and the
            z-[120] listing preview and concierge sheets it opens from. */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[130] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-label={`Photos of ${title}`}
          className="fixed inset-0 z-[130] flex flex-col bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            Photos of {title}
          </DialogPrimitive.Title>

          {/* Sticky header */}
          <div className="sticky top-0 z-20 flex items-center justify-between h-14 px-3 sm:px-5 bg-background border-b border-border">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full p-2 hover:bg-muted transition-colors"
              aria-label="Close photos"
            >
              <X className="h-5 w-5 text-foreground" />
            </button>
            <h2 className="font-serif text-base sm:text-lg font-medium truncate px-3">
              Photo tour
            </h2>
            <div className="w-9" /> {/* spacer for symmetry */}
          </div>

          <TourBody groups={groups} onOpenZoom={openZoom} />

          {viewMode === "zoom" && photos[zoomIndex] && (
            <ZoomLayer
              photo={photos[zoomIndex]}
              index={zoomIndex}
              total={photos.length}
              onClose={closeZoom}
              onPrev={prevZoom}
              onNext={nextZoom}
              title={title}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

interface TourBodyProps {
  groups: PhotoGroup[];
  onOpenZoom: (photoIndex: number) => void;
}

const TourBody = ({ groups, onOpenZoom }: TourBodyProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [activeKey, setActiveKey] = useState<string>(groups[0]?.key ?? "");

  // Scroll-spy: pick the section that's closest to the middle of the
  // scroll container. Using rootMargin on IntersectionObserver narrows the
  // viewport to a middle band so the highlight tracks the user's eye.
  useEffect(() => {
    if (groups.length === 0) return;
    const root = scrollRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        // Pick the entry whose intersection ratio is highest.
        const best = visible.reduce((a, b) =>
          a.intersectionRatio > b.intersectionRatio ? a : b,
        );
        const id = (best.target as HTMLElement).dataset.areaKey;
        if (id) setActiveKey(id);
      },
      {
        root,
        rootMargin: "-45% 0px -45% 0px",
        threshold: [0, 0.5, 1],
      },
    );
    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [groups]);

  const handleNavClick = (key: string) => {
    setActiveKey(key);
    const el = sectionRefs.current[key];
    const container = scrollRef.current;
    if (!el || !container) return;
    // smooth-scroll the container, not the page
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset = elRect.top - containerRect.top + container.scrollTop - 16;
    container.scrollTo({ top: offset, behavior: "smooth" });
  };

  if (groups.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        No photos yet.
      </div>
    );
  }

  return (
    <div className="flex-1 flex min-h-0">
      {/* Desktop sticky nav */}
      <aside className="hidden md:block w-56 lg:w-64 shrink-0 border-r border-border overflow-y-auto py-6 px-2">
        <nav className="space-y-1">
          {groups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => handleNavClick(g.key)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-lg text-[13px] flex items-center justify-between gap-2 transition-colors",
                activeKey === g.key
                  ? "bg-foreground text-background"
                  : "hover:bg-muted text-foreground/85",
              )}
            >
              <span className="truncate">{g.label}</span>
              <span
                className={cn(
                  "text-[11px] tabular-nums",
                  activeKey === g.key ? "text-background/70" : "text-muted-foreground",
                )}
              >
                {g.items.length}
              </span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Scroll container */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {/* Mobile chip row, sticky to top of scroll container */}
        <div className="md:hidden sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
          <div className="flex gap-1.5 overflow-x-auto px-3 py-2 scrollbar-none">
            {groups.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => handleNavClick(g.key)}
                className={cn(
                  "shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap",
                  activeKey === g.key
                    ? "bg-foreground text-background"
                    : "bg-muted text-foreground/80",
                )}
              >
                {g.label}
                <span className="ml-1.5 opacity-70">{g.items.length}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-10 space-y-10 sm:space-y-14">
          {groups.map((g) => (
            <section
              key={g.key}
              data-area-key={g.key}
              ref={(el) => {
                sectionRefs.current[g.key] = el;
              }}
              id={`area-${g.key}`}
              className="scroll-mt-4"
            >
              <h3 className="font-serif text-xl sm:text-2xl font-medium mb-4">
                {g.label}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {g.items.map(({ photo, photoIndex }) => (
                  <button
                    key={`${g.key}-${photoIndex}`}
                    type="button"
                    onClick={() => onOpenZoom(photoIndex)}
                    className="relative aspect-[4/3] rounded-card overflow-hidden bg-muted group"
                    aria-label={`Open photo ${photoIndex + 1}`}
                  >
                    <img
                      src={transformImage(photo.url, { width: 800, height: 600 })}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ZoomLayerProps {
  photo: PhotoEntry;
  index: number;
  total: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  title: string;
}

const ZoomLayer = ({
  photo,
  index,
  total,
  onClose,
  onPrev,
  onNext,
  title,
}: ZoomLayerProps) => (
  <div
    className="absolute inset-0 z-30 flex items-center justify-center bg-charcoal/95"
    onClick={onClose}
    role="dialog"
    aria-label={`Photo ${index + 1} of ${total}`}
  >
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      className="absolute top-4 right-4 z-10 h-11 w-11 rounded-full bg-white text-charcoal shadow-lg flex items-center justify-center hover:bg-white/95 active:scale-[0.97] transition-all"
      aria-label="Close photo"
    >
      <X className="h-5 w-5" />
    </button>

    {total > 1 && (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-white text-charcoal shadow-lg flex items-center justify-center hover:bg-white/95 active:scale-[0.97] transition-all"
          aria-label="Previous photo"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-white text-charcoal shadow-lg flex items-center justify-center hover:bg-white/95 active:scale-[0.97] transition-all"
          aria-label="Next photo"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </>
    )}

    <img
      src={transformImage(photo.url, { width: 1920, resize: "contain" })}
      alt={`${title} photo ${index + 1}`}
      onClick={(e) => e.stopPropagation()}
      className="max-h-[88vh] max-w-[94vw] object-contain rounded-xl"
    />

    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-charcoal/80 text-white text-[12px] font-medium backdrop-blur-sm">
      {index + 1} / {total}
    </div>
  </div>
);

export default PhotoTourModal;
